import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Award, Users, Lightbulb, Shield, ArrowRight
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useTeam } from '../../context/TeamContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';


function AboutPage() {
  const { loading: settingsLoading } = useSettings();
  const { team, loading: teamLoading } = useTeam();

  const values = [
    { 
      icon: Award, 
      title: 'Excellence', 
      desc: 'Nous visons l\'excellence dans chaque projet, en garantissant la qualité et la performance de nos solutions.' 
    },
    { 
      icon: Lightbulb, 
      title: 'Innovation', 
      desc: 'Nous restons à la pointe des technologies pour offrir des solutions innovantes et performantes.' 
    },
    { 
      icon: Shield, 
      title: 'Intégrité', 
      desc: 'Nous agissons avec transparence et éthique dans toutes nos interactions professionnelles.' 
    },
  ];

  if (settingsLoading || teamLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              À Propos de Nous
            </h1>
            <p className="text-xl text-dark-300">
              Votre partenaire de confiance pour la transformation digitale bancaire
            </p>
          </motion.div>
        </div>
      </section>

      {/* Qui sommes-nous + Stats */}
      <section className="section bg-white py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Qui sommes-nous - Gauche */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-dark-900 mb-6">
                Qui sommes-nous ?
              </h2>
              <p className="text-dark-600 mb-6 leading-relaxed">
                DDEC (Digital Dream Expert Consulting) est le leader de la transformation digitale 
                bancaire en Afrique francophone. Avec plus de 15 ans d'expérience, nous 
                accompagnons les institutions financières dans leur modernisation et leur 
                conformité aux normes internationales.
              </p>
              <p className="text-dark-600 leading-relaxed">
                Notre équipe d'experts certifiés maîtrise les principales solutions Core Banking et 
                les standards internationaux. Nous offrons un accompagnement 360° depuis l'audit 
                initial jusqu'à la formation de vos équipes, avec une assistance continue.
              </p>
            </motion.div>

            {/* Stats - Droite */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-dark-900 rounded-2xl p-8"
            >
              <div className="space-y-6">
                <div className="border-b border-dark-700 pb-6">
                  <div className="text-4xl font-bold text-white mb-1">15+</div>
                  <p className="text-dark-400">Années d'expérience</p>
                </div>
                <div className="border-b border-dark-700 pb-6">
                  <div className="text-4xl font-bold text-white mb-1">50+</div>
                  <p className="text-dark-400">Projets réalisés</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-white mb-1">200+</div>
                  <p className="text-dark-400">Professionnels formés</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nos Valeurs */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-dark-900">
              Nos Valeurs
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-dark-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-dark-900 mb-3">{value.title}</h3>
                <p className="text-dark-600 text-sm leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    {/* Notre Équipe */}
<section className="section bg-white py-16">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mb-12"
    >
      <h2 className="text-3xl font-bold text-dark-900 mb-4 italic">
        Notre Équipe
      </h2>
      <p className="text-dark-500">
        Des experts passionnés et certifiés à votre service
      </p>
    </motion.div>

    {!team || team.length === 0 ? (
      <div className="text-center py-12">
        <Users className="w-16 h-16 mx-auto mb-4 text-dark-300" />
        <p className="text-dark-500">L'équipe sera affichée ici une fois ajoutée depuis le panel admin.</p>
      </div>
    ) : (
      <div className="flex flex-wrap justify-center gap-8">
        {team.filter(member => member !== null && member !== undefined).map((member, index) => (
          <motion.div
            key={member?.id || index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="w-full max-w-sm border border-dark-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Header avec initiale */}
            <div className="bg-[#1a3254] py-8 text-center">
              {member?.image_url ? (
                <img 
                  src={member.image_url} 
                  alt={member?.name || 'Membre'}
                  className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-white"
                />
              ) : (
                <div className="flex items-center justify-center">
                  <span className="text-5xl font-bold text-white">
                    {member?.name?.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Contenu */}
            <div className="p-6 text-center">
              <h3 className="text-lg font-bold text-dark-900 mb-1">
                {member?.name || 'Nom inconnu'}
              </h3>
              <p className="text-primary-600 text-sm font-medium mb-3">
                {member?.position || 'Poste non défini'}
              </p>
              <p className="text-dark-500 text-sm leading-relaxed">
                {member?.bio || ''}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    )}
  </div>
</section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: 'rgb(26, 50, 84)' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à travailler avec nous ?
            </h2>
            <p className="text-gray-300 mb-8">
              Demandez un devis gratuit et découvrez comment nous pouvons vous accompagner dans 
              votre transformation digitale.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium"
            >
              Demande de devis
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;